import { useRef } from 'react';
import { useMatch } from 'react-router-dom';
import { useAudioPlayerState } from 'shared/hooks/useAudioPlayer';
import { useInlineVisibility } from 'shared/hooks/useInlineVisibility';
import { cn } from 'shared/utils/cn';
import type { PublishedEpisode } from 'shared/data/episodes';
import { PlayerShell } from './PlayerShell';

interface AudioPlayerProps {
  mode?: 'inline';
  episode?: PublishedEpisode;
}

export function AudioPlayer({ mode, episode }: AudioPlayerProps) {
  if (mode === 'inline' && episode) {
    return <InlinePlayer episode={episode} />;
  }
  return <StickyPlayer />;
}

function InlinePlayer({ episode }: { episode: PublishedEpisode }) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useInlineVisibility(rootRef);
  return (
    <div ref={rootRef}>
      <PlayerShell variant="inline" episode={episode} />
    </div>
  );
}

function StickyPlayer() {
  const match = useMatch('/episodes/:id');
  const { currentEpisode, inlineVisible } = useAudioPlayerState();

  if (!currentEpisode) return null;

  const onCurrentEpisodeDetail =
    match?.params.id !== undefined && match.params.id === currentEpisode.number;
  const hidden = onCurrentEpisodeDetail && inlineVisible;
  const variant: 'compact' | 'full' = onCurrentEpisodeDetail ? 'compact' : 'full';

  return (
    <div
      className={cn(
        'fixed bottom-24 md:bottom-0 left-0 w-full z-[80] p-4 lg:p-6',
        'transition-[opacity,transform] duration-200 ease-out',
        hidden
          ? 'opacity-0 translate-y-2 pointer-events-none'
          : 'opacity-100 translate-y-0',
      )}
      aria-hidden={hidden}
    >
      <div className="max-w-6xl mx-auto pointer-events-auto">
        <PlayerShell variant={variant} episode={currentEpisode} />
      </div>
    </div>
  );
}
